import React, { useEffect, useState, useRef } from "react";
import ZoomVideo from "@zoom/videosdk";
import { Prober } from "@zoom/probesdk";
import { fetchVideoSDKToken, saveVideoSDK } from "../../services/zoomService";
import MicLevelMeter from "./MicLevelMeter";
import Header from "../layout/Header";
import { VideoQuality } from "@zoom/videosdk";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Loader from "./Loader";

const BG_URL = "/bgmobile.png";
const BG_URL_DESKTOP = "/background.jpg"; // MISMO fondo que en escritorio
const DEBUG = true;
const log = (...a) => DEBUG && console.info("[ZOOM-REC]", ...a);
const warn = (...a) => DEBUG && console.warn("[ZOOM-REC]", ...a);
const err = (...a) => DEBUG && console.error("[ZOOM-REC]", ...a);

// 360p (menor tráfico). Si el enum cambia, fallback a 2.
const QUALITY_360P = (VideoQuality && (VideoQuality.Video_360P ?? 2)) ?? 2;

// Detecciones simples
const UA = typeof navigator !== "undefined" ? navigator.userAgent : "";
const isSafari =
  /Safari\//.test(UA) && !/Chrome\//.test(UA) && !/Edg\//.test(UA);

// Elegir el MIME adecuado para MediaRecorder
function pickRecorderMime() {
  const candidates = [
    "video/webm;codecs=vp9",
    "video/webm;codecs=vp8",
    "video/webm",
    "video/mp4;codecs=h264,aac",
    "video/mp4",
  ];
  for (const mime of candidates) {
    try {
      if (window.MediaRecorder && MediaRecorder.isTypeSupported?.(mime))
        return mime;
    } catch {}
  }
  // Safari puede crear MediaRecorder sin mime (tomará MP4 por defecto)
  return undefined;
}

// Pausas cortas
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Normaliza valores obtenidos del storage (evita "undefined"/"null" como cadenas)
const normalizeVal = (v) => {
  if (!v) return null;
  const s = String(v).trim();
  if (s === "undefined" || s === "null") return null;
  return s;
};

// --- Shadow DOM helpers ---
function queryShadow(el, selector) {
  try {
    if (!el || !el.shadowRoot) return null;
    return el.shadowRoot.querySelector(selector);
  } catch {
    return null;
  }
}

function findZoomRenderedMedia(player) {
  try {
    // 0) Dummy global que crea el SDK (cuando es <video>)
    const dummy = document.getElementById(
      "__ZOOM_SDK_DUMMY_HIDDEN_SELF_VIEW_VIDEO_ID__"
    );
    if (dummy instanceof HTMLVideoElement) return dummy;

    // 1) Host real del Shadow DOM
    const host =
      player?.closest?.("video-player-container") ||
      document.querySelector("video-player-container");
    const shadowCandidate = queryShadow(
      host,
      'canvas[id^="video-player-canvas-"], canvas, video'
    );
    if (
      shadowCandidate instanceof HTMLCanvasElement ||
      shadowCandidate instanceof HTMLVideoElement
    ) {
      log(
        "findZoomRenderedMedia: Shadow DOM →",
        shadowCandidate.tagName,
        shadowCandidate.id || "(sin id)"
      );
      return shadowCandidate;
    }

    // 2) Fallback a hack que funcionó (internals no documentados)
    try {
      const legacy = document.getElementsByTagName("video-player-container")[0]
        ?.children?.[0];
      if (legacy?.container?.canvas) {
        log("findZoomRenderedMedia: legacy container.canvas");
        return legacy.container.canvas;
      }
    } catch {}

    // 3) El propio player podría ser <video>/<canvas>
    if (
      player instanceof HTMLVideoElement ||
      player instanceof HTMLCanvasElement
    )
      return player;

    // 4) O tener hijos en light DOM
    const child = player?.querySelector?.("video,canvas");
    if (child instanceof HTMLVideoElement || child instanceof HTMLCanvasElement)
      return child;

    return null;
  } catch (e) {
    warn("findZoomRenderedMedia error:", e);
    return null;
  }
}

// Espera hasta que findZoomRenderedMedia localice un <video>/<canvas> utilizable
const waitForMedia = (player, timeoutMs = 8000) =>
  new Promise((resolve, reject) => {
    const t0 = performance.now();
    const tick = () => {
      try {
        const media = findZoomRenderedMedia(player);
        if (media) return resolve(media);
        if (performance.now() - t0 > timeoutMs)
          return reject(
            new Error(
              "⏰ No se encontró <video>/<canvas> a tiempo (incluyendo Shadow DOM)"
            )
          );
        requestAnimationFrame(tick);
      } catch (e) {
        reject(e);
      }
    };
    tick();
  });

// Asegura que el <video> esté listo y en reproducción (autoplay a veces es quisquilloso)
async function ensureVideoReadyAndPlayable(video) {
  try {
    if (!(video instanceof HTMLVideoElement)) return;
    video.muted = true; // evita bloqueos de autoplay
    video.playsInline = true;
    if (video.readyState < 2) {
      await new Promise((res) =>
        video.addEventListener("loadeddata", res, { once: true })
      );
    }
    try {
      await video.play();
    } catch (e) {
      // No es crítico para poder capturar en la mayoría de casos
      warn("ensureVideoReadyAndPlayable: play() rechazado", e?.message);
    }
  } catch {}
}

// Crea un canvas “espejo” del <video> y devuelve { canvas, stop }
function createCanvasMirrorFromVideo(
  video,
  { width = 640, height = 360, fps = 30 } = {}
) {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  canvas.style.position = "fixed";
  canvas.style.top = "-10000px";
  canvas.style.left = "-10000px";
  document.body.appendChild(canvas);

  const ctx = canvas.getContext("2d");
  let stopped = false;
  let rafId = null;
  let rVFCancel = null;

  const useRVFC = typeof video.requestVideoFrameCallback === "function";

  const draw = () => {
    if (stopped) return;
    try {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    } catch (e) {}
    rafId = requestAnimationFrame(draw);
  };

  const rvfcCb = () => {
    if (stopped) return;
    try {
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    } catch {}
    rVFCancel = video.requestVideoFrameCallback(rvfcCb);
  };

  if (useRVFC) {
    rVFCancel = video.requestVideoFrameCallback(rvfcCb);
  } else {
    rafId = requestAnimationFrame(draw);
  }

  const stop = () => {
    stopped = true;
    try {
      rafId && cancelAnimationFrame(rafId);
    } catch {}
    try {
      useRVFC && rVFCancel && video.cancelVideoFrameCallback?.(rVFCancel);
    } catch {}
    try {
      canvas.remove();
    } catch {}
  };

  log("Canvas mirror creado (useRVFC=", useRVFC, ")");
  return { canvas, stop };
}

// Construye un stream final {video + mic}, eliminando duplicados de audio
function buildFinalStream(capture, micTrack) {
  try {
    capture?.getAudioTracks?.().forEach((t) => capture.removeTrack(t));
  } catch {}
  const final = new MediaStream();
  const v = capture?.getVideoTracks?.()[0];
  if (v) final.addTrack(v);
  if (micTrack) final.addTrack(micTrack);
  return final;
}

// === Gestión de intentos por usuario (repeticiones) ===
const MAX_REPEATS = 3;
const attemptsKey = (email) =>
  `zoomrec_attempts_${(email || "").toLowerCase()}`;
const readAttempts = (email) => {
  try {
    const raw = sessionStorage.getItem(attemptsKey(email));
    const n = parseInt(raw, 10);
    if (Number.isFinite(n)) return Math.max(0, n);
  } catch {}
  try {
    sessionStorage.setItem(attemptsKey(email), String(MAX_REPEATS));
  } catch {}
  return MAX_REPEATS;
};
const decAttempts = (email) => {
  const key = attemptsKey(email);
  const cur = readAttempts(email);
  const next = Math.max(0, cur - 1);
  try {
    sessionStorage.setItem(key, String(next));
  } catch {}
  return next;
};

// Limpia llaves viejas con otro prefijo (por si quedaron de builds anteriores)
function purgeLegacyAttemptKeys() {
  try {
    const toRemove = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith("zoomrec_attempts_candidate_")) toRemove.push(k);
    }
    toRemove.forEach((k) => sessionStorage.removeItem(k));
  } catch {}
}

const ZoomSession = ({ sessionName, userName, userIdentity }) => {
  const [sendingVideo, setSendingVideo] = useState(false);
  const [showTensorFlowPreview, setShowTensorFlowPreview] = useState(false);
  const [zoomClient, setZoomClient] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [loaderData, setLoaderData] = useState({
    loading: false,
    message: "",
  });
  const [timeLeft, setTimeLeft] = useState(120);
  const [recordStream, setRecordStream] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [devices, setDevices] = useState([]);
  const [audioInputId, setAudioInputId] = useState("");
  const [videoInputId, setVideoInputId] = useState("");
  const [isReviewing, setIsReviewing] = useState(false);
  const [reviewUrl, setReviewUrl] = useState(null);
  const reviewBlobRef = useRef(null);
  const [submitted, setSubmitted] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(MAX_REPEATS);
  const [repeatDisabled, setRepeatDisabled] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const timerRef = useRef(null);
  const compositePreviewRef = useRef(null);
  const elapsedTimerRef = useRef(null);
  const autoStopTimeoutRef = useRef(null);
  const cameraPreviewRef = useRef(null);
  const cameraPreviewStreamRef = useRef(null);
  const localAudioStreamRef = useRef(null); // solo para flujo móvil puro
  const zoomPreviewRef = useRef(null);
  const recordingRef = useRef(false);
  const startLockRef = useRef(false);
  const ssEmailRef = useRef(null);
  const ssNameRef = useRef(null);
  const stopLoopTensorFLow = useRef(false);
  const userIdRef = useRef(null);

  // Review state (post-record)

  // Session storage values for identity

  const navigate = useNavigate();

  // Para detener el bucle del canvas-mirror en cleanup
  const mirrorStopRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const hasSubmitted = sessionStorage.getItem("videoSubmitted") === "true";
    if (hasSubmitted) {
      navigate("/thank-you");
    }
  }, [navigate]);

  // ---------- Helpers: preview ----------
  const stopPreviewTracks = () => {
    try {
      const stream = cameraPreviewRef.current?.srcObject;
      if (stream && stream.getTracks) {
        stream.getTracks().forEach((t) => t.stop());
        cameraPreviewRef.current.srcObject = null;
        cameraPreviewStreamRef.current = null;
        log("Preview tracks stopped");
      }
    } catch (e) {
      warn("stopPreviewTracks error:", e);
    }
  };

  const startPreview = async (desiredDeviceId, audioInputId) => {
    const background = isMobile ? BG_URL : BG_URL_DESKTOP;
    try {
      const constraints = {
        video: desiredDeviceId
          ? {
              deviceId: { exact: desiredDeviceId },
              width: { ideal: 640, max: 960 },
              height: { ideal: 360, max: 540 },
              frameRate: { ideal: 30, max: 30 },
            }
          : {
              facingMode: { ideal: "user" },
              width: { ideal: 640, max: 960 },
              height: { ideal: 360, max: 540 },
              frameRate: { ideal: 30, max: 30 },
            },
        audio: audioInputId
          ? {
              DeviceId: { exact: audioInputId },
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            }
          : {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      cameraPreviewStreamRef.current = stream;
      if (cameraPreviewRef.current) {
        cameraPreviewRef.current.srcObject = stream;
        cameraPreviewRef.current.onloadedmetadata = () => {
          cameraPreviewRef.current
            .play()
            .catch((e) => warn("Preview play() interrupted:", e));
        };
      }
      setLoaderData({
        loading: true,
        message: "Charging preview",
      });

      if (showTensorFlowPreview) {
        await startPreviewVideoTensorFlow(background, stream);
      } else {
        zoomClient &&
          (await startPreviewVideoZoom(desiredDeviceId, audioInputId));
      }
      setLoaderData({
        loading: false,
        message: "",
      });
    } catch (e) {
      setLoaderData({
        loading: false,
        message: "",
      });
      err("Camera preview error:", e);
    }
  };

  // ---------- Helpers: timers & cleanup ----------
  const clearTimers = () => {
    try {
      if (timerRef.current) clearInterval(timerRef.current);
      if (elapsedTimerRef.current) clearInterval(elapsedTimerRef.current);
      if (autoStopTimeoutRef.current) clearTimeout(autoStopTimeoutRef.current);
      timerRef.current = null;
      elapsedTimerRef.current = null;
      autoStopTimeoutRef.current = null;
    } catch {}
  };

  const cleanup = async (
    stream,
    player,
    userId,
    { resumePreview = false } = {}
  ) => {
    log("cleanup: start");
    try {
      mirrorStopRef.current?.();
      mirrorStopRef.current = null;
    } catch {}

    // 1) Detach primero; si el meeting ya cerró, ignorar fallas
    try {
      if (stream && userId && player) {
        await stream.detachVideo(userId, player);
        log("cleanup: detachVideo OK");
      }
    } catch (e) {
      warn("cleanup: detachVideo falló", e);
    }

    // 2) Intentar parar video (puede lanzar si ya está cerrado)
    try {
      await stream?.stopVideo?.();
    } catch (e) {
      warn("cleanup: stopVideo falló", e);
    }

    try {
      player?.remove?.();
    } catch (e) {
      warn("cleanup: remove player falló", e);
    }

    try {
      localAudioStreamRef.current?.getTracks?.().forEach((t) => t.stop());
      localAudioStreamRef.current = null;
    } catch {}

    try {
      await zoomClient?.leave?.();
      log("cleanup: leave OK");
    } catch (e) {
      warn("cleanup: leave falló", e);
    }
    clearTimers();
    setIsRecording(false);

    if (resumePreview) startPreview(videoInputId);

    log("cleanup: done");
  };

  // ---------- Effects ----------
  useEffect(() => {
    try {
      purgeLegacyAttemptKeys();
      const rawEmail =
        normalizeVal(sessionStorage.getItem("login_email")) ||
        normalizeVal(sessionStorage.getItem("login_email"));
      const rawName =
        normalizeVal(sessionStorage.getItem("login_name")) ||
        normalizeVal(sessionStorage.getItem("login_name"));
      const email = rawEmail ? rawEmail.trim().toLowerCase() : null;
      const name = rawName ? rawName.trim() : null;
      ssEmailRef.current = email; // do NOT fall back to userIdentity
      ssNameRef.current = name || userName || null;
      log("Identity resolved:", {
        email: ssEmailRef.current,
        name: ssNameRef.current,
      });
    } catch {}
    setAttemptsLeft(readAttempts(ssEmailRef.current));
  }, [userName]);

  useEffect(() => {
    const initDevices = async () => {
      try {
        const prober = new Prober();
        await prober.requestMediaDevicePermission({ audio: true, video: true });
        const { devices } = await prober.requestMediaDevices();
        setDevices(devices);
        log("Devices:", devices);

        const defMic = devices.find((d) => d.kind === "audioinput");
        const defCam = devices.find((d) => d.kind === "videoinput");
        if (defMic) setAudioInputId(defMic.deviceId);
        if (defCam) setVideoInputId(defCam.deviceId);
      } catch (e) {
        err("Device fetch error:", e);
      }
    };
    initDevices();
  }, []);

  useEffect(() => {
    const clientInstance = ZoomVideo.createClient();
    setZoomClient(clientInstance);
    clientInstance
      .init("en-US", "Global", { leaveOnPageUnload: true })
      .then(() => log("Zoom SDK init OK"))
      .catch((error) => err("Zoom SDK initialization error", error));

    return () => {
      try {
        if (zoomClient && zoomClient.getSessionInfo()) clientInstance.leave();
      } catch {}
      try {
        ZoomVideo.destroyClient();
      } catch {}
      setZoomClient(null);
      stopPreviewTracks();
      clearTimers();
      try {
        if (reviewUrl) URL.revokeObjectURL(reviewUrl);
      } catch {}
    };
  }, []);

  // Reflejar cambio de cámara en preview cuando NO estamos dentro de sesión ni en review
  useEffect(() => {
    if (zoomClient) zoomClient?.leave();
    if (isReviewing || submitted) return;
    startPreview(videoInputId, audioInputId);
  }, [
    isMobile,
    videoInputId,
    isReviewing,
    submitted,
    audioInputId,
    showTensorFlowPreview,
  ]);

  async function loadSegmenter() {
    const bodySeg = await import("@tensorflow-models/body-segmentation");
    const segmenter = await bodySeg.createSegmenter(
      bodySeg.SupportedModels.MediaPipeSelfieSegmentation,
      {
        runtime: "mediapipe",
        solutionPath:
          "https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation",
        modelType: "general", // 'landscape' si prefieres exteriores
      }
    );
    return { bodySeg, segmenter };
  }

  const startRecordingPrep = async () => {
    setIsRecording(true);
    let counter = 3;
    setLoaderData({
      loading: true,
      message: "Get ready, the video will start recording in..." + counter,
    });
    const interval = setInterval(async () => {
      counter -= 1;
      setLoaderData({
        loading: true,
        message: "Get ready, the video will start recording in..." + counter,
      });
      if (counter === 0) {
        setLoaderData({
          loading: false,
          message: "",
        });
        clearInterval(interval);
        await startRecording();
      }
    }, 1000);
  };
  const startRecording = async () => {
    if (startLockRef.current || isRecording) {
      warn("startRecording: lock");
      return;
    }
    startLockRef.current = true;

    // Decrementa intentos al iniciar grabación (modelo actual)
    const emailNow = (
      normalizeVal(sessionStorage.getItem("login_email")) ||
      normalizeVal(sessionStorage.getItem("login_email")) ||
      ssEmailRef.current ||
      ""
    ).toLowerCase();
    if (emailNow) setAttemptsLeft(decAttempts(emailNow));

    setIsReviewing(false);
    stopPreviewTracks();

    try {
      const chosenMime = pickRecorderMime();
      log("MediaRecorder mime =", chosenMime || "(browser default)");

      const chunks = [];
      const recOptions = chosenMime ? { mimeType: chosenMime } : undefined;
      const recorder = new MediaRecorder(recordStream, recOptions);
      setMediaRecorder(recorder);

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size) {
          chunks.push(e.data);
          log("ondataavailable: chunk size=", e.data.size);
        }
      };
      recorder.onstart = () => log("MediaRecorder: start");
      recorder.onstop = async () => {
        try {
          log("onstop: assembling blob");
          const useMp4 = (chosenMime && chosenMime.includes("mp4")) || isSafari;
          const type = useMp4 ? "video/mp4" : "video/webm";
          const blob = new Blob(chunks, { type });
          if (!blob.size) {
            warn("Grabación vacía (0 bytes)");
          } else {
            const url = URL.createObjectURL(blob);
            reviewBlobRef.current = blob;
            setReviewUrl(url);
            setIsReviewing(true);
            log("Review ready:", { type, size: blob.size });
          }
        } catch (e) {
          err("onstop error:", e);
        } finally {
          try {
            await recordStream?.stopVideo?.();
          } catch (e) {
            warn("stopVideo falló:", e);
          }
          try {
            await sleep(50);
          } catch {}
          await cleanup(
            recordStream,
            zoomPreviewRef.current,
            userIdRef.current,
            {
              resumePreview: false,
            }
          );
        }
      };

      recorder.start(1000);
      recordingRef.current = true;
      setLoaderData({
        loading: false,
        message: "",
      });

      setTimeLeft(120);
      setElapsedTime(0);
      clearTimers();
      timerRef.current = setInterval(
        () => setTimeLeft((p) => (p > 0 ? p - 1 : 0)),
        1000
      );
      elapsedTimerRef.current = setInterval(
        () => setElapsedTime((e) => e + 1),
        1000
      );
      autoStopTimeoutRef.current = setTimeout(() => {
        if (recorder.state === "recording") {
          log("Auto-stop tras 60000 ms");
          recorder.stop();
        }
      }, 120000);
    } catch (e) {
      console.log(e);
      alert(e?.message || "No fue posible iniciar la grabación");
      clearTimers();
      setIsRecording(false);
      try {
        startPreview(videoInputId);
      } catch {}
    } finally {
      setLoaderData({
        loading: false,
        message: "",
      });
      startLockRef.current = false;
    }
  };

  const stopRecording = () => {
    try {
      stopLoopTensorFLow.current = true;
      if (
        mediaRecorder &&
        mediaRecorder.state === "recording" &&
        elapsedTime >= 6
      ) {
        log("stopRecording manual (elapsed =", elapsedTime, "s)");
        mediaRecorder.stop();
        setIsRecording(false);
        recordingRef.current = false;
      } else {
        warn(
          "stopRecording: no se puede parar aún.",
          "estado=",
          mediaRecorder?.state,
          "elapsed=",
          elapsedTime
        );
      }
    } catch (e) {
      err("stopRecording error:", e);
    }
  };

  const handleRepeat = async () => {
    if (repeatDisabled) return;
    setRepeatDisabled(true);
    try {
      if (reviewUrl) URL.revokeObjectURL(reviewUrl);
    } catch {}
    try {
      localAudioStreamRef.current?.getTracks?.().forEach((t) => t.stop());
    } catch {}
    localAudioStreamRef.current = null;

    window.location.reload();
  };

  const handleSend = async () => {
    setLoaderData({
      loading: true,
      message: "Sending video please wait",
    });
    if (!reviewBlobRef.current || submitted) return;
    try {
      const blob = reviewBlobRef.current;
      const ext = blob.type.includes("mp4") ? "mp4" : "webm";
      const fd = new FormData();
      fd.append("File", blob, `grabacion.${ext}`);

      // Identidad desde storage
      const emailVal =
        normalizeVal(sessionStorage.getItem("login_email")) ||
        normalizeVal(sessionStorage.getItem("login_email")) ||
        ssEmailRef.current ||
        "";
      const nameVal =
        normalizeVal(sessionStorage.getItem("login_name")) ||
        normalizeVal(sessionStorage.getItem("login_name")) ||
        ssNameRef.current ||
        "";
      if (nameVal) fd.append("UserName", nameVal);
      if (emailVal) fd.append("EmailAddress", emailVal);
      setSendingVideo(true);
      const result = await saveVideoSDK(fd);
      log("Archivo enviado al backend", { emailVal, nameVal, result });

      if (result.success) {
        setSendingVideo(false);
        sessionStorage.setItem("videoSubmitted", "true");
        setSubmitted(true);
      } else {
        setSendingVideo(false);
        toast.error(result.errors[0]);
      }
    } catch (e) {
      err("Upload error:", e);
      toast.error(
        e.message || "We couldn't send your video. Please try again."
      );
    }
    setLoaderData({
      loading: false,
      message: "",
    });
  };

  const startPreviewVideoZoom = async (videoInputId, audioInputId) => {
    const token = await fetchVideoSDKToken(sessionName, 1, userIdentity);
    const container = zoomPreviewRef.current;
    if (!token) throw new Error("No hay token de sesión");

    log("join →", { sessionName, userName });
    await zoomClient.join(sessionName, token, userName, "", true);

    const stream = zoomClient.getMediaStream();

    try {
      log("startVideo con VB (forzado en desktop)...");
      await stream.startVideo({
        ...(videoInputId ? { cameraDeviceId: videoInputId } : {}),
        virtualBackground: { imageUrl: BG_URL_DESKTOP },
      });
      await stream.switchCamera(videoInputId);
    } catch (e) {
      setShowTensorFlowPreview(true);
      zoomClient.leave();
      return;
    }

    const me = zoomClient.getCurrentUserInfo() || {};
    const userId = me.userId;
    userIdRef.current = userId;

    const player = await stream.attachVideo(userId, QUALITY_360P);
    player.classList.add("video-cell");
    container.innerHTML = "";
    container.appendChild(player);

    const mediaElem = await waitForMedia(player, 8000);
    await ensureVideoReadyAndPlayable(mediaElem);

    let captureStream = null;
    if (typeof mediaElem.captureStream === "function") {
      captureStream = mediaElem.captureStream(30);
    } else if (
      mediaElem instanceof HTMLVideoElement &&
      HTMLCanvasElement.prototype.captureStream
    ) {
      const { canvas, stop } = createCanvasMirrorFromVideo(mediaElem, {
        width: 640,
        height: 360,
        fps: 30,
      });
      mirrorStopRef.current = stop;
      captureStream = canvas.captureStream(30);
    }

    try {
      localAudioStreamRef.current?.getTracks?.().forEach((t) => t.stop());
    } catch {}
    const audioConstraints = audioInputId
      ? {
          deviceId: { exact: audioInputId },
          noiseSuppression: true,
          echoCancellation: true,
          autoGainControl: true,
        }
      : {
          noiseSuppression: true,
          echoCancellation: true,
          autoGainControl: true,
        };
    localAudioStreamRef.current = await navigator.mediaDevices.getUserMedia({
      audio: audioConstraints,
    });
    const micTrack = localAudioStreamRef.current.getAudioTracks()[0];

    const finalStream = buildFinalStream(captureStream, micTrack);
    setRecordStream(finalStream);
  };

  const startPreviewVideoTensorFlow = async (imageUrl, camerashowice) => {
    try {
      setShowTensorFlowPreview(true);
      if (!navigator.mediaDevices?.getUserMedia)
        throw new Error("No camera/mic.");

      // 📷 Camera & mic config

      const gum = camerashowice;
      const vid = document.createElement("video");
      vid.srcObject = gum;
      vid.muted = true;
      vid.playsInline = true;

      try {
        await vid.play();
      } catch (e) {
        console.log(e);
      }

      // ✅ Ensure real video dimensions before sizing canvas/mask
      if (!vid.videoWidth || !vid.videoHeight) {
        await new Promise((res) => {
          const handler = () => {
            vid.removeEventListener("loadedmetadata", handler);
            res();
          };
          vid.addEventListener("loadedmetadata", handler);
        });
      }

      const vw = vid.videoWidth || 640;
      const vh = vid.videoHeight || 360;
      console.log(vw, vh);

      // 🖼️ Offscreen canvas for composition
      const canvas = document.createElement("canvas");
      canvas.width = vw;
      canvas.height = vh;
      canvas.style.position = "fixed";
      canvas.style.left = "-10000px";
      canvas.style.top = "-10000px";
      document.body.appendChild(canvas);
      const ctx = canvas.getContext("2d");

      // 🌄 Background image with CORS
      const bg = new Image();
      bg.crossOrigin = "anonymous";
      bg.src = imageUrl;
      await bg.decode();

      function drawCover(targetCtx, img, W, H) {
        const r = Math.max(W / img.width, H / img.height);
        const nw = img.width * r;
        const nh = img.height * r;
        const nx = (W - nw) / 2;
        const ny = (H - nh) / 2;
        targetCtx.drawImage(img, nx, ny, nw, nh);
      }

      // 🧠 Segmenter
      const { bodySeg, segmenter } = await loadSegmenter();
      const maskCanvas = document.createElement("canvas");
      maskCanvas.width = canvas.width;
      maskCanvas.height = canvas.height;
      const maskCtx = maskCanvas.getContext("2d");

      // Vars de segmentación
      let lastMask = null;
      let lastSegAt = 0;

      async function loop() {
        if (stopLoopTensorFLow.current) return;

        const now = performance.now();

        // Recalcular máscara ~15 FPS
        if (!lastMask || now - lastSegAt > 150) {
          const people = await segmenter.segmentPeople(vid, {
            multiSegmentation: false,
          });

          // ⚠️ CLAVE: máscara con alpha (persona opaca, fondo transparente)
          // toMask(people, foregroundRGBA, backgroundRGBA, drawContour?, threshold)
          const FG = { r: 0, g: 0, b: 0, a: 255 }; // color da igual; importa el alpha=255
          const BG = { r: 0, g: 0, b: 0, a: 0 }; // totalmente transparente fuera de la persona
          lastMask = await bodySeg.toBinaryMask(people, FG, BG, false, 0.5);

          // Pásala al maskCanvas para poder usar drawImage (sí respeta compositing)
          maskCtx.putImageData(lastMask, 0, 0);

          lastSegAt = now;
        }

        // 0) Limpia frame
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // 1) Dibuja el frame del video (si quieres espejo frontal, descomenta el bloque flip)
        // ctx.save();
        // ctx.translate(canvas.width, 0);
        // ctx.scale(-1, 1);
        ctx.drawImage(vid, 0, 0, canvas.width, canvas.height);
        // ctx.restore();

        // 2) Aplica la máscara (conserva SOLO la persona)
        ctx.globalCompositeOperation = "destination-in";
        ctx.drawImage(maskCanvas, 0, 0);

        // 3) Fondo por detrás de la persona (evita “negro” en transparencia)
        ctx.save();
        ctx.globalCompositeOperation = "destination-over";
        drawCover(ctx, bg, canvas.width, canvas.height);
        ctx.restore();

        // 4) Vuelve a normal
        ctx.globalCompositeOperation = "source-over";

        requestAnimationFrame(loop);
      }
      requestAnimationFrame(loop);

      // 🎬 Final composition stream (canvas video + mic audio)
      const vTrack = canvas.captureStream(30).getVideoTracks()[0];
      const aTrack = gum.getAudioTracks()[0];
      const finalStream = new MediaStream([vTrack, aTrack]);

      // 👀 Composite preview
      if (compositePreviewRef.current) {
        compositePreviewRef.current.srcObject = finalStream;
        try {
          await compositePreviewRef.current.play();
        } catch {}
      }
      setRecordStream(finalStream);
    } catch (error) {
      toast.warn(error.message);
    }
  };

  // Texto de intentos (visibilidad: siempre en UI)
  const AttemptsBadge = () => (
    <div style={{ marginTop: 8, fontSize: "0.95rem", color: "#666" }}>
      Attempts left: <strong>{attemptsLeft}</strong>
    </div>
  );

  return (
    <div className="zoom-session">
      <Header backDirection={"/instructions"} isRecording={isRecording} />
      <h1 className="green-font App martop-1rem marbo-1rem">
        Introduce yourself and share your experience
      </h1>

      <div
        className={`flex fwrap fhor-center fver-center white-bkg rounded card gap1`}
      >
        <div className="flex fdir-col fver-center fhor-center">
          {!isRecording && !isReviewing && !submitted && (
            <div className="device-selectors">
              <h2 className="App gray-font bold-font">Select your devices!</h2>
              <p
                style={{
                  maxWidth: "30vmax",
                  textAlign: "center",
                  margin: "0 auto",
                }}
              >
                Choose the microphone and camera you want to use. When you're
                ready, click on 'Record Now' to start your presentation.
              </p>
              <div
                style={{
                  display: "flex",
                  gap: "1rem",
                  margin: "1rem 0",
                  placeContent: "center",
                }}
              >
                <div className="flex fdir-col fhor-center fver-center">
                  <div className="mic-info-container">
                    <label>Microphone:</label>
                    <select
                      onChange={(e) => setAudioInputId(e.target.value)}
                      value={audioInputId}
                    >
                      <option value="">Select a mic</option>
                      {devices
                        .filter((d) => d.kind === "audioinput")
                        .map((d) => (
                          <option key={d.deviceId} value={d.deviceId}>
                            {d.label || d.deviceId}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="cam-info-container">
                    <label>Camera:</label>
                    <select
                      onChange={(e) => setVideoInputId(e.target.value)}
                      value={videoInputId}
                    >
                      <option value="">Select a camera</option>
                      {devices
                        .filter((d) => d.kind === "videoinput")
                        .map((d) => (
                          <option key={d.deviceId} value={d.deviceId}>
                            {d.label || d.deviceId}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>

                {!isReviewing && !submitted && <MicLevelMeter />}
              </div>
            </div>
          )}
        </div>

        <div
          style={{
            padding: "1rem 0",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div>
            <div
              style={{ display: isReviewing || submitted ? "none" : "block" }}
            >
              {/* NUEVO: preview del compuesto mientras grabas */}
              {showTensorFlowPreview ? (
                <video
                  ref={compositePreviewRef}
                  autoPlay
                  muted
                  playsInline
                  style={{
                    display: "block",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "12px",
                  }}
                />
              ) : (
                <video-player-container
                  ref={zoomPreviewRef}
                  id="zoom-host"
                  className="video-player-container flex fdir-col fhor-center marbo-1rem  video-shell"
                />
              )}
            </div>
          </div>

          <div className="flex fdir-col fver-center fhor-center">
            {!isReviewing && !submitted ? (
              <>
                <div className="timer">
                  Time left: {Math.floor(timeLeft / 60)}:
                  {String(timeLeft % 60).padStart(2, "0")}
                </div>
                <p className="gray-font" style={{ marginBottom: "0px" }}>
                  <strong> Answer duration: up to 2 minute</strong>
                </p>
                <AttemptsBadge />
                <div>
                  {!isRecording ? (
                    <button
                      onClick={startRecordingPrep}
                      disabled={
                        loaderData.loading || isRecording || !attemptsLeft
                      }
                    >
                      Record Now
                    </button>
                  ) : (
                    <button onClick={stopRecording} disabled={elapsedTime < 6}>
                      Stop
                    </button>
                  )}
                </div>
              </>
            ) : !submitted ? (
              <>
                <video
                  controls
                  src={reviewUrl}
                  style={{
                    maxWidth: "660px",
                    width: "100%",
                    marginBottom: "0.75rem",
                  }}
                />
                <div
                  className="flex fhor-center fver-center"
                  style={{ gap: "0.75rem" }}
                >
                  {attemptsLeft >= 1 && (
                    <button
                      onClick={handleRepeat}
                      disabled={repeatDisabled || sendingVideo}
                    >
                      Repeat
                    </button>
                  )}
                  <button onClick={handleSend} disabled={sendingVideo}>
                    Send
                  </button>
                </div>
                <AttemptsBadge />
              </>
            ) : (
              // SUBMIT CONFIRMATION STATE
              <>
                <video
                  controls
                  src={reviewUrl}
                  style={{
                    maxWidth: "660px",
                    width: "100%",
                    marginBottom: "0.75rem",
                  }}
                />
                <div
                  style={{
                    background: "#e8faf0",
                    border: "1px solid #b7e4c7",
                    color: "#0a6b3a",
                    padding: "12px 14px",
                    borderRadius: 12,
                    textAlign: "center",
                    maxWidth: 660,
                  }}
                >
                  <strong>Success!</strong> Your video was sent and we have
                  received your submission.
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Loader show={loaderData.loading} message={loaderData.message} />
    </div>
  );
};

export default ZoomSession;
